import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Reveal } from "@/components/demo/lib/Reveal";
import { useCounter } from "@/components/demo/lib/useCounter";
import { useReveal } from "@/components/demo/lib/useReveal";
import { IMG } from "@/components/demo/lib/images";

const WA = "41765008176";

const SYMPTOMS = [
  { id: "skin", label: "Pelle e dermatologia", treatments: ["Visita dermatologica", "Mappatura nei (epiluminescenza)", "Laser CO2 frazionato"] },
  { id: "aging", label: "Segni del tempo", treatments: ["Botox", "Filler dermici", "HIFU lifting non chirurgico"] },
  { id: "body", label: "Corpo e silhouette", treatments: ["Radiofrequenza", "Criolipolisi", "Linfodrenaggio manuale"] },
  { id: "wellness", label: "Wellness e prevenzione", treatments: ["Check-up completo", "Biorivitalizzazione", "Infusione vitaminica IV"] },
];

const DOCTORS = [
  { n: "Dr.ssa Elena Conti", s: "Dermatologia", c: "FMH Dermatologia · USI", lang: ["IT", "EN", "FR"] },
  { n: "Dr. Marco Vassalli", s: "Medicina estetica", c: "Allergan Diamond · FMH", lang: ["IT", "EN", "DE"] },
  { n: "Dr.ssa Sofia Müller", s: "Endocrinologia", c: "FMH · MAS Wellness", lang: ["DE", "EN", "IT"] },
  { n: "Dr. Andrea Rossi", s: "Chirurgia plastica", c: "FMH Chir. Plastica · Mayo fellow", lang: ["IT", "EN"] },
];

const FAQ = [
  { q: "Devo essere assicurato per prenotare?", a: "No. Effettuiamo anche visite private. Per chi è assicurato, siamo convenzionati con tutte le principali casse malati svizzere LAMal e complementari." },
  { q: "Posso prenotare per un familiare?", a: "Sì, è sufficiente indicare i dati anagrafici al momento della prenotazione. Per i minori serve la presenza di un genitore o di un tutore legale." },
  { q: "In quanto tempo ricevo una visita?", a: "Per la maggior parte delle specialità garantiamo disponibilità entro 5–7 giorni. Per le urgenze cliniche è previsto un canale dedicato con risposta entro 2 ore." },
  { q: "Quali documenti devo portare?", a: "Carta d'identità, tessera di assicurazione malattia e gli eventuali referti precedenti relativi alla visita. Tutto il resto lo predisponiamo noi." },
];

function Pulse({ v, suffix = "", label }: { v: number; suffix?: string; label: string }) {
  const { ref, visible } = useReveal<HTMLDivElement>();
  const c = useCounter(v, visible);
  return (
    <div ref={ref} className="text-center">
      <div className="font-semibold text-4xl text-sky-700 tabular-nums">{Math.round(c).toLocaleString("it-CH")}{suffix}</div>
      <div className="text-xs text-slate-500 mt-2">{label}</div>
    </div>
  );
}

function Salus() {
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const sym = useMemo(() => SYMPTOMS.find((s) => s.id === picked), [picked]);
  const [filter, setFilter] = useState<string>("Tutte");
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased overflow-x-hidden">
      <style>{`body { font-family: 'Inter', system-ui, sans-serif }`}</style>

      <header className="fixed top-4 inset-x-4 z-50 max-w-6xl mx-auto">
        <div className="backdrop-blur-xl bg-white/70 border border-slate-200/60 shadow-lg shadow-sky-900/5 rounded-full px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-sky-500 to-cyan-400 grid place-items-center text-white text-sm font-bold">S</div>
            <div className="font-semibold tracking-tight">Clinica Salus</div>
          </div>
          <nav className="hidden md:flex gap-7 text-sm text-slate-600">
            <a href="#specialties" className="hover:text-sky-700">Specialità</a>
            <a href="#check" className="hover:text-sky-700">Trova lo specialista</a>
            <a href="#doctors" className="hover:text-sky-700">Medici</a>
            <a href="#insurance" className="hover:text-sky-700">Assicurazioni</a>
          </nav>
          <a href="#book" className="bg-sky-700 hover:bg-sky-800 text-white text-sm px-4 py-2 rounded-full transition">Prenota una visita</a>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-32 pb-20 px-6 sm:px-10 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-40 -left-40 h-[480px] w-[480px] rounded-full bg-sky-200/40 blur-3xl" />
          <div className="absolute top-20 right-0 h-[420px] w-[420px] rounded-full bg-cyan-200/40 blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[3fr_2fr] gap-12 items-end">
          <div>
            <div className="inline-flex items-center gap-2 text-xs text-slate-600 bg-white border border-slate-200 px-3 py-1.5 rounded-full mb-6">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> 12 specialisti disponibili · Visite oggi
            </div>
            <h1 className="text-5xl sm:text-7xl lg:text-[5.5vw] font-semibold tracking-[-0.03em] leading-[1.02] break-words">
              La tua salute,<br /><span className="text-sky-700">al massimo livello.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed">
              Centro medico polispecialistico FMH a Lugano. 12 specialità mediche sotto un solo tetto, prenotazione online in 30 secondi e convenzioni con tutte le casse malati svizzere.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#book" className="bg-sky-700 hover:bg-sky-800 text-white px-7 py-4 rounded-full font-medium transition">Prenota una visita</a>
              <a href="#check" className="border border-slate-300 hover:border-sky-700 px-7 py-4 rounded-full font-medium transition">Trova il tuo specialista →</a>
            </div>
          </div>
          <Reveal delay={150}>
            <div className="rounded-3xl overflow-hidden aspect-square shadow-2xl shadow-sky-900/10">
              <img src={IMG.clinic.hero} alt="Reception e interno della Clinica Salus a Lugano" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="px-6 sm:px-10 py-10 border-y border-slate-100">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          <Pulse v={12} label="Specialità mediche" />
          <Pulse v={28000} label="Visite all'anno" />
          <Pulse v={98} suffix="%" label="Pazienti soddisfatti" />
          <Pulse v={15} label="Anni di esperienza" />
        </div>
      </section>

      {/* SYMPTOM CHECKER */}
      <section id="check" className="px-6 sm:px-10 py-24">
        <div className="max-w-5xl mx-auto bg-gradient-to-br from-sky-50 to-cyan-50/60 rounded-3xl p-8 sm:p-12 border border-sky-100">
          <Reveal>
            <div className="text-xs tracking-widest uppercase text-sky-700 mb-3">3 step · 30 secondi</div>
            <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight break-words">Trova lo specialista giusto per te.</h2>
            <p className="text-slate-600 mt-3 max-w-lg">Tre semplici domande per indirizzarti al medico più adatto. Risultato immediato, nessuna registrazione richiesta.</p>
          </Reveal>

          <div className="mt-10 flex gap-2 mb-8">
            {[0, 1, 2].map((s) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-all ${s <= step ? "bg-sky-700" : "bg-slate-200"}`} />
            ))}
          </div>

          {step === 0 && (
            <div>
              <div className="text-sm font-medium text-slate-700 mb-4">1. Cosa ti porta da noi?</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {SYMPTOMS.map((s) => (
                  <button key={s.id} onClick={() => { setPicked(s.id); setStep(1); }} className={`text-left p-5 rounded-2xl border transition ${picked === s.id ? "border-sky-700 bg-white shadow-md" : "border-slate-200 bg-white hover:border-sky-400"}`}>
                    <div className="font-medium">{s.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="text-sm font-medium text-slate-700 mb-4">2. Da quanto tempo è in corso?</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {["Meno di 1 settimana", "1–4 settimane", "1–6 mesi", "Oltre 6 mesi"].map((opt) => (
                  <button key={opt} onClick={() => setStep(2)} className="p-4 rounded-2xl border border-slate-200 bg-white hover:border-sky-400 transition text-sm">{opt}</button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && sym && (
            <div>
              <div className="text-sm font-medium text-slate-700 mb-4">3. Trattamenti consigliati</div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="text-xs uppercase tracking-widest text-sky-700 mb-2">Suggerito per te</div>
                <div className="text-2xl font-semibold mb-4">{sym.label}</div>
                <ul className="space-y-2">
                  {sym.treatments.map((t) => (
                    <li key={t} className="flex items-center gap-3 text-slate-700">
                      <span className="h-2 w-2 rounded-full bg-sky-700" />{t}
                    </li>
                  ))}
                </ul>
                <a href="#book" className="mt-6 inline-block bg-sky-700 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-sky-800 transition">Prenota una visita →</a>
                <button onClick={() => { setStep(0); setPicked(null); }} className="ml-3 text-sm text-slate-500 hover:text-slate-800">Ricomincia</button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SPECIALTIES */}
      <section id="specialties" className="px-6 sm:px-10 py-24">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="grid sm:grid-cols-2 gap-8 mb-16 items-end">
              <div>
                <div className="text-xs tracking-widest uppercase text-sky-700 mb-3">Specialità</div>
                <h2 className="text-4xl sm:text-6xl font-semibold tracking-tight break-words">12 specialità sotto un solo tetto.</h2>
              </div>
              <p className="text-slate-600 max-w-md">Cardiologia, dermatologia, endocrinologia, ginecologia, medicina estetica, medicina interna, oculistica, ortopedia, pediatria, psicologia, radiologia e urologia.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {["Cardiologia", "Dermatologia", "Endocrinologia", "Ginecologia", "Medicina estetica", "Medicina interna", "Oculistica", "Ortopedia", "Pediatria", "Psicologia", "Radiologia", "Urologia"].map((s, i) => (
              <Reveal key={s} delay={i * 30}>
                <div className="aspect-square p-5 rounded-2xl bg-slate-50 hover:bg-sky-50 border border-slate-100 hover:border-sky-200 transition flex flex-col justify-between">
                  <div className="h-8 w-8 rounded-lg bg-white border border-slate-200 grid place-items-center text-sky-700">+</div>
                  <div className="font-medium leading-tight">{s}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DOCTORS */}
      <section id="doctors" className="px-6 sm:px-10 py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight break-words">I nostri medici</h2>
              <div className="flex gap-2 flex-wrap">
                {["Tutte", "Dermatologia", "Medicina estetica", "Endocrinologia"].map((f) => (
                  <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-sm border transition ${filter === f ? "bg-sky-700 text-white border-sky-700" : "bg-white border-slate-200 hover:border-sky-400"}`}>{f}</button>
                ))}
              </div>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {DOCTORS.filter((d) => filter === "Tutte" || d.s === filter).map((d, i) => (
              <Reveal key={d.n} delay={i * 50}>
                <div className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg hover:shadow-sky-900/5 transition">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-br from-sky-100 to-cyan-100 grid place-items-center text-sky-700 font-semibold mb-4">{d.n.split(" ").slice(-2).map(p => p[0]).join("")}</div>
                  <div className="font-semibold">{d.n}</div>
                  <div className="text-sm text-sky-700 mt-1">{d.s}</div>
                  <div className="text-xs text-slate-500 mt-3">{d.c}</div>
                  <div className="flex gap-1 mt-4">{d.lang.map((l) => <span key={l} className="text-[10px] tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{l}</span>)}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSURANCE */}
      <section id="insurance" className="px-6 sm:px-10 py-20">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-xs tracking-widest uppercase text-sky-700 mb-4">Assicurazioni convenzionate</div>
            <h3 className="text-3xl font-semibold mb-10 max-w-xl">Convenzionati con tutte le maggiori casse malati svizzere.</h3>
          </Reveal>
          <div className="flex flex-wrap gap-x-12 gap-y-6 items-center text-slate-400">
            {["SWICA", "CSS", "Helsana", "Sanitas", "Visana", "Groupe Mutuel", "Concordia"].map((p) => (
              <div key={p} className="text-2xl font-semibold tracking-tight">{p}</div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 sm:px-10 py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-xs tracking-widest uppercase text-sky-700 mb-4">Domande frequenti</div>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-10 break-words">Le risposte ai vostri dubbi.</h2>
          </Reveal>
          <div className="space-y-2">
            {FAQ.map((f, i) => (
              <button key={i} onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left bg-white border border-slate-200 hover:border-sky-300 rounded-2xl p-6 block transition">
                <div className="flex justify-between items-center gap-4">
                  <div className="font-semibold">{f.q}</div>
                  <div className={`text-2xl text-sky-700 transition-transform ${openFaq === i ? "rotate-45" : ""}`}>+</div>
                </div>
                <div className={`overflow-hidden transition-all ${openFaq === i ? "max-h-40 mt-4" : "max-h-0"}`}>
                  <p className="text-slate-600 leading-relaxed">{f.a}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* BOOK */}
      <section id="book" className="px-6 sm:px-10 py-24 bg-gradient-to-br from-sky-700 to-cyan-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-6xl font-semibold tracking-tight break-words">Prenota oggi.<br />Visita questa settimana.</h2>
          <p className="mt-6 text-sky-50 max-w-xl mx-auto">Tempo medio di risposta: 12 minuti. Disponibilità entro 7 giorni per la maggior parte delle specialità.</p>
          <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Buongiorno Clinica Salus, vorrei prenotare una visita.")}`} className="mt-10 inline-block bg-white text-sky-700 px-10 py-5 rounded-full font-semibold hover:bg-slate-100 transition">Prenota su WhatsApp</a>
        </div>
      </section>

      <footer className="px-6 sm:px-10 py-10 text-xs text-slate-500">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-between gap-4">
          <div>Clinica Salus · Via Pretorio 9, 6900 Lugano · Autorizzazione DSS-TI</div>
          <div>Demo FlashWeb · <Link to="/" className="underline">flash-web.ch</Link></div>
        </div>
      </footer>
    </div>
  );
}

export const Route = createFileRoute("/demo/clinica-salus")({
  head: () => ({
    meta: [
      { title: "Clinica Salus — Centro medico a Lugano (Demo FlashWeb)" },
      { name: "description", content: "Centro medico polispecialistico a Lugano: 12 specialità FMH, prenotazione online, assicurazioni convenzionate. Specialisti disponibili in giornata." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: Salus,
});
